import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import BooleanIcon from "veneer/BooleanIcon";
import Button from "veneer/Button";
import DataTable, {
  AppliedFilter,
  AvailableFilterOption,
  BulkActionBarItem,
  PageSize,
  createColumnHelper,
  sorting,
} from "veneer/DataTable";

import { alertsSelector } from "cortex/ui/src/fleet/redux-store/alerts-v3/selectors";
import { useAppSelector } from "cortex/ui/src/hooks";
import { useFeatureFlag } from "cortex/ui/src/hooks/useFeatureFlag";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import {
  isReadonly,
  toString as userToString,
  usersSelector,
} from "cortex/ui/src/redux-store/users/selectors";
import { DATA_TABLE_DATE_RANGE_PRESETS } from "cortex/ui/src/ui/filter/DataTableFilters";
import { LoadingIndicator } from "cortex/ui/src/ui/loader";
import { formatDateTimeLocal } from "cortex/ui/src/ui/table/formats";
import { Option } from "cortex/ui/src/ui/table/types";
import { sortByLabelAsc } from "cortex/ui/src/util/sortByProp";
import { Incident } from "cortex/ui/src/zeus/view/incidents/model";

import CommentIncidentsDrawer from "./CommentIncidentsDrawer";
import GroupedIncidentsTable from "./GroupedIncidentsTable";
import IncidentDetailsDrawer from "./IncidentDetailsDrawer";
import IncidentPriority, { getIncidentPriorityLabel } from "./IncidentPriority";
import IncidentStatus, { INCIDENT_STATUS_OPTIONS } from "./IncidentStatus";
import IncidentsTableFilters, {
  IncidentFilterId,
  createDefaultIncidentTableFilters,
  getIncidentTableSettingsKey,
  isWithinDateRange,
  normalizeIncidentTableFilters,
  usePersistedIncidentTableFilters,
} from "./IncidentsTableFilters";
import {
  formatNotified,
  formatResolveDetails,
} from "./IncidentTableFormatters";
import ResolveIncidentDrawer from "./ResolveIncidentDrawer";

interface IncidentsTableProps {
  grouped?: boolean;
  hiddenColumns?: ReadonlyArray<string>;
  incidents: ReadonlyArray<Incident>;
  onCloseIncident?: () => void;
  onOpenIncident?: (incidentId: number) => void;
  routeSelectedIncident?: Incident | null;
  showFilters?: boolean;
  sizePerPage?: PageSize;
  storesLoaded?: boolean;
  type?: "resolved" | "unresolved";
}

const column = createColumnHelper<Incident>();

const AVAILABLE_FILTERS: ReadonlyArray<AvailableFilterOption> = [
  { id: "priority", label: "Priority" },
  { id: "status", label: "Status" },
  { id: "name", label: "Name" },
  { id: "device", label: "Device" },
  { id: "firstOccurrence", label: "First Occurrence" },
  { id: "lastNotified", label: "Last Notified" },
  { id: "notified", label: "Notified" },
];

export default function IncidentsTable({
  grouped = true,
  hiddenColumns = [],
  incidents,
  onCloseIncident,
  onOpenIncident,
  routeSelectedIncident,
  showFilters = false,
  sizePerPage = 25,
  storesLoaded,
  type,
}: IncidentsTableProps) {
  const isGroupedTriggeredAlertsEnabled = useFeatureFlag(
    "is-grouped-triggered-alerts",
  );
  const alerts = useAppSelector(alertsSelector);
  const users = useAppSelector(usersSelector);
  const user = useAppSelector(getAuthUser);
  const accountId = user?.account_id ?? 0;
  const userIsReadonly = isReadonly(user);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(
    null,
  );
  const [showIncidentDrawer, setShowIncidentDrawer] = useState(false);
  const [showIncidentDrawerWithComment, setShowIncidentDrawerWithComment] =
    useState(false);
  const [selectedItems, setSelectedItems] = useState<ReadonlyArray<Incident>>(
    [],
  );
  const [showResolveDrawer, setShowResolveDrawer] = useState(false);
  const [showCommentDrawer, setShowCommentDrawer] = useState(false);
  const [incidentsToResolve, setIncidentsToResolve] = useState<
    ReadonlyArray<Incident>
  >([]);
  const { filters, setFilters } = usePersistedIncidentTableFilters(
    showFilters,
    accountId,
  );
  const tableAnchorRef = useRef<HTMLDivElement>(null);
  const tableSettingsKey = getIncidentTableSettingsKey(accountId);
  const canFilterByStatus =
    isGroupedTriggeredAlertsEnabled && type === "unresolved";
  const availableFilters = useMemo(
    () =>
      AVAILABLE_FILTERS.filter(
        ({ id }) =>
          (id !== "priority" || isGroupedTriggeredAlertsEnabled) &&
          (id !== "status" || canFilterByStatus),
      ),
    [canFilterByStatus, isGroupedTriggeredAlertsEnabled],
  );
  const visibleFilters = useMemo(
    () =>
      filters.visibleFilters.filter((id) =>
        availableFilters.some((option) => option.id === id),
      ),
    [availableFilters, filters.visibleFilters],
  );
  const visibleFilterState = useMemo(
    () => ({
      ...filters,
      priority: isGroupedTriggeredAlertsEnabled ? filters.priority : [],
      visibleFilters,
    }),
    [filters, isGroupedTriggeredAlertsEnabled, visibleFilters],
  );

  useEffect(() => {
    if (isGroupedTriggeredAlertsEnabled) {
      return;
    }

    setFilters((previous) => {
      if (
        previous.priority.length === 0 &&
        !previous.visibleFilters.includes("priority")
      ) {
        return previous;
      }

      return {
        ...previous,
        priority: [],
        visibleFilters: previous.visibleFilters.filter(
          (id) => id !== "priority",
        ),
      };
    });
  }, [isGroupedTriggeredAlertsEnabled, setFilters]);

  const getOptions = useCallback(
    (getValue: (incident: Incident) => string | undefined) => {
      const values = new Set<string>();

      incidents.forEach((incident) => {
        const value = getValue(incident);
        if (value) {
          values.add(value);
        }
      });

      return Array.from(values)
        .map((value) => ({ label: value, value }))
        .sort(sortByLabelAsc);
    },
    [incidents],
  );

  const getIncidentName = useCallback(
    (incident: Incident) =>
      isGroupedTriggeredAlertsEnabled
        ? (incident.alert_name_snapshot ?? "")
        : incident.name,
    [isGroupedTriggeredAlertsEnabled],
  );

  const getIncidentPriority = useCallback(
    (incident: Incident) =>
      isGroupedTriggeredAlertsEnabled
        ? (incident.alert_priority_snapshot ?? undefined)
        : (alerts[incident.alert_id]?.priority ?? undefined),
    [alerts, isGroupedTriggeredAlertsEnabled],
  );

  const nameOptions = useMemo(
    () => getOptions(getIncidentName),
    [getIncidentName, getOptions],
  );
  const deviceOptions = useMemo(
    () => getOptions(({ device_name }) => device_name),
    [getOptions],
  );
  const notifiedOptions = useMemo<ReadonlyArray<Option<string>>>(() => {
    const userIds = new Set<string>();
    incidents.forEach(({ notified = [] }) => {
      notified.forEach((userId) => userIds.add(String(userId)));
    });

    return Array.from(userIds)
      .map((userId) => ({
        label: userToString(users[userId], userId),
        value: userId,
      }))
      .sort(sortByLabelAsc);
  }, [incidents, users]);
  const priorityOptions = useMemo<ReadonlyArray<Option<string>>>(() => {
    return getOptions(getIncidentPriority)
      .map(({ value }) => ({
        label: getIncidentPriorityLabel(value),
        value,
      }))
      .sort(sortByLabelAsc);
  }, [getIncidentPriority, getOptions]);

  const filteredIncidents = useMemo(
    () =>
      incidents.filter((incident) => {
        const notifiedUserIds = (incident.notified ?? []).map(String);

        return (
          (filters.name.length === 0 ||
            filters.name.includes(getIncidentName(incident))) &&
          (filters.device.length === 0 ||
            filters.device.includes(incident.device_name)) &&
          (filters.notified.length === 0 ||
            filters.notified.every((userId) =>
              notifiedUserIds.includes(userId),
            )) &&
          (!isGroupedTriggeredAlertsEnabled ||
            filters.priority.length === 0 ||
            filters.priority.includes(getIncidentPriority(incident) ?? "")) &&
          (!canFilterByStatus ||
            filters.status.length === 0 ||
            (incident.status && filters.status.includes(incident.status))) &&
          isWithinDateRange(incident.create_date, filters.firstOccurrence) &&
          isWithinDateRange(incident.last_notified, filters.lastNotified)
        );
      }),
    [canFilterByStatus, filters, incidents, isGroupedTriggeredAlertsEnabled],
  );

  const handleVisibleFiltersChange = useCallback(
    (selectedFilterIds: ReadonlyArray<string>) => {
      const visibleFilters = selectedFilterIds.filter(
        (id): id is IncidentFilterId =>
          availableFilters.some((option) => option.id === id),
      );

      setFilters((previous) => ({
        ...previous,
        device: visibleFilters.includes("device") ? previous.device : [],
        firstOccurrence: visibleFilters.includes("firstOccurrence")
          ? previous.firstOccurrence
          : [null, null],
        firstOccurrencePreset: visibleFilters.includes("firstOccurrence")
          ? previous.firstOccurrencePreset
          : null,
        lastNotified: visibleFilters.includes("lastNotified")
          ? previous.lastNotified
          : [null, null],
        lastNotifiedPreset: visibleFilters.includes("lastNotified")
          ? previous.lastNotifiedPreset
          : null,
        name: visibleFilters.includes("name") ? previous.name : [],
        notified: visibleFilters.includes("notified") ? previous.notified : [],
        priority: visibleFilters.includes("priority") ? previous.priority : [],
        status: visibleFilters.includes("status") ? previous.status : [],
        visibleFilters,
      }));
    },
    [availableFilters, setFilters],
  );

  const clearFilters = useCallback(() => {
    setFilters((previous) => ({
      ...createDefaultIncidentTableFilters(),
      visibleFilters: previous.visibleFilters,
    }));
  }, []);

  const handleApplySavedFilters = useCallback(
    (savedFilters: unknown) => {
      const normalizedFilters = normalizeIncidentTableFilters(savedFilters);
      setFilters(
        isGroupedTriggeredAlertsEnabled
          ? normalizedFilters
          : {
              ...normalizedFilters,
              priority: [],
              visibleFilters: normalizedFilters.visibleFilters.filter(
                (id) => id !== "priority",
              ),
            },
      );
    },
    [isGroupedTriggeredAlertsEnabled, setFilters],
  );

  const getAppliedFilters = useCallback(
    (
      id: "device" | "name" | "notified" | "priority" | "status",
      label: string,
      options: ReadonlyArray<Option<string>>,
    ): ReadonlyArray<AppliedFilter> =>
      filters[id].map((selectedValue) => ({
        key: `${id}-${selectedValue}`,
        label,
        onDelete: () =>
          setFilters((previous) => ({
            ...previous,
            [id]: previous[id].filter((value) => value !== selectedValue),
          })),
        value:
          options.find(({ value }) => value === selectedValue)?.label ??
          selectedValue,
      })),
    [filters],
  );

  const getDateAppliedFilter = useCallback(
    (
      id: "firstOccurrence" | "lastNotified",
      label: string,
    ): ReadonlyArray<AppliedFilter> => {
      const [startDate, endDate] = filters[id];
      if (!visibleFilters.includes(id) || !startDate || !endDate) {
        return [];
      }

      const dateFormat = "MMM Do, YYYY hh:mm A";
      const absoluteRange = `${formatDateTimeLocal(
        startDate,
        dateFormat,
      )} - ${formatDateTimeLocal(endDate, dateFormat)}`;
      const presetKey =
        id === "firstOccurrence"
          ? "firstOccurrencePreset"
          : "lastNotifiedPreset";
      const presetId = filters[presetKey];
      const presetLabel = DATA_TABLE_DATE_RANGE_PRESETS.find(
        ({ id: optionId }) => optionId === presetId,
      )?.label;

      return [
        {
          key: id,
          label,
          onDelete: () =>
            setFilters((previous) => ({
              ...previous,
              [id]: [null, null],
              [presetKey]: null,
            })),
          value: presetLabel
            ? `${presetLabel} (${absoluteRange})`
            : absoluteRange,
        },
      ];
    },
    [filters, setFilters, visibleFilters],
  );

  const appliedFilters = useMemo(
    () => [
      ...getDateAppliedFilter("firstOccurrence", "First Occurrence"),
      ...getDateAppliedFilter("lastNotified", "Last Notified"),
      ...getAppliedFilters("name", "Name", nameOptions),
      ...(isGroupedTriggeredAlertsEnabled
        ? getAppliedFilters("priority", "Priority", priorityOptions)
        : []),
      ...(canFilterByStatus
        ? getAppliedFilters("status", "Status", INCIDENT_STATUS_OPTIONS)
        : []),
      ...getAppliedFilters("device", "Device", deviceOptions),
      ...getAppliedFilters("notified", "Notified", notifiedOptions),
    ],
    [
      deviceOptions,
      getDateAppliedFilter,
      getAppliedFilters,
      canFilterByStatus,
      isGroupedTriggeredAlertsEnabled,
      nameOptions,
      notifiedOptions,
      priorityOptions,
    ],
  );

  const filterOptions = useMemo(
    () => (
      <IncidentsTableFilters
        deviceOptions={deviceOptions}
        nameOptions={nameOptions}
        notifiedOptions={notifiedOptions}
        onChange={setFilters}
        priorityOptions={priorityOptions}
        value={visibleFilterState}
      />
    ),
    [
      deviceOptions,
      nameOptions,
      notifiedOptions,
      priorityOptions,
      visibleFilterState,
    ],
  );

  const tableFilterProps = showFilters
    ? {
        appliedFilters: {
          filters: appliedFilters,
          onClearAll: clearFilters,
        },
        availableFilters: {
          filters: availableFilters,
          onChange: handleVisibleFiltersChange,
          selectedFilterIds: visibleFilters,
        },
        dataTestId: {
          appliedFilters: "triggered-alerts-applied-filters",
          availableFilters: "triggered-alerts-available-filters",
          savedFilters: "triggered-alerts-saved-filters",
        },
        savedFilters: {
          currentFilters: visibleFilterState,
          onApply: handleApplySavedFilters,
        },
        settingsKey: tableSettingsKey,
        viewOptions: visibleFilters.length > 0 && filterOptions,
      }
    : {};

  useEffect(() => {
    const incidentIds = new Set(filteredIncidents.map(({ id }) => String(id)));

    setSelectedItems((prevSelectedItems) =>
      prevSelectedItems.filter(({ id }) => incidentIds.has(String(id))),
    );
  }, [filteredIncidents]);

  useEffect(() => {
    if (routeSelectedIncident === undefined) {
      return;
    }

    setSelectedIncident(routeSelectedIncident);
    setShowIncidentDrawer(routeSelectedIncident !== null);

    if (routeSelectedIncident === null) {
      setShowIncidentDrawerWithComment(false);
    }
  }, [routeSelectedIncident]);

  useEffect(() => {
    if (!selectedIncident) {
      return;
    }

    if (routeSelectedIncident !== undefined) {
      return;
    }

    const incidentIds = new Set(filteredIncidents.map(({ id }) => String(id)));
    if (!incidentIds.has(String(selectedIncident.id))) {
      setSelectedIncident(null);
      setShowIncidentDrawer(false);
      setShowIncidentDrawerWithComment(false);
    }
  }, [filteredIncidents, routeSelectedIncident, selectedIncident]);

  const handleOpenSingle = useCallback(
    (incident: Incident, showResolutionCommentByDefault = false) => {
      if (isGroupedTriggeredAlertsEnabled) {
        setSelectedIncident(incident);
        setShowIncidentDrawerWithComment(showResolutionCommentByDefault);
        setShowIncidentDrawer(true);
        onOpenIncident?.(incident.id);
        return;
      }

      setIncidentsToResolve([incident]);
      setShowResolveDrawer(true);
    },
    [isGroupedTriggeredAlertsEnabled, onOpenIncident],
  );

  const handleOpenResolveAll = useCallback(
    (selectedIncidents: ReadonlyArray<Incident>) => {
      if (selectedIncidents.length === 0) {
        return;
      }

      setIncidentsToResolve(selectedIncidents);
      setShowResolveDrawer(true);
    },
    [],
  );

  const handleOpenSelected = useCallback(() => {
    if (selectedItems.length === 0) {
      return;
    }
    setIncidentsToResolve(selectedItems);
    setShowResolveDrawer(true);
  }, [selectedItems]);

  const handleOpenComment = useCallback(() => {
    if (selectedItems.length === 0) {
      return;
    }

    setShowCommentDrawer(true);
  }, [selectedItems]);

  const handleClose = useCallback(() => {
    setIncidentsToResolve([]);
    setShowResolveDrawer(false);
  }, []);

  const handleCloseCommentDrawer = useCallback(() => {
    setShowCommentDrawer(false);
  }, []);

  const handleCloseIncidentDrawer = useCallback(() => {
    setSelectedIncident(null);
    setShowIncidentDrawer(false);
    setShowIncidentDrawerWithComment(false);
    onCloseIncident?.();
  }, [onCloseIncident]);

  const getIncidentRowProps = useCallback(
    (incident: Incident) => {
      return {
        hover: true,
        onClick: (event: MouseEvent<HTMLTableRowElement>) => {
          const target = event.target as HTMLElement;
          const isVeneerCell = target.closest(
            'th[data-component="veneer-table-cell"], td[data-component="veneer-table-cell"]',
          );

          if (!isVeneerCell) {
            return;
          }

          if (target.closest("input[type='checkbox']")) {
            return;
          }

          if (
            target.closest(
              "a, button, [role='button'], [role='menuitem'], input, select, textarea, label",
            )
          ) {
            return;
          }

          handleOpenSingle(incident);
        },
      };
    },
    [handleOpenSingle],
  );

  const columns = useMemo(() => {
    const baseColumns = [
      column.accessor(getIncidentName, {
        id: "name",
        header: "Name",
        cell: (prop) => prop.getValue(),
        sortingFn: "alphanumeric",
      }),
      column.accessor("status", {
        header: "Status",
        cell: (prop) => <IncidentStatus status={prop.getValue()} />,
        sortingFn: "alphanumeric",
      }),
      column.accessor("create_date", {
        header: "First Occurrence Date",
        cell: (prop) => formatDateTimeLocal(prop.getValue() ?? ""),
        sortingFn: sorting.datetime((row) => row.create_date),
        meta: { width: 200 },
      }),
      column.accessor("last_notified", {
        header: "Last Notified Date",
        cell: (prop) => formatDateTimeLocal(prop.getValue() ?? ""),
        sortingFn: sorting.datetime((row) => row.last_notified),
        meta: { width: 200 },
      }),
      column.accessor("device_name", {
        header: "Device Name",
        cell: (prop) => {
          const row = prop.row.original;
          const value = prop.getValue();
          return <Link to={`/devices/${row.device_device_id}`}>{value}</Link>;
        },
        sortingFn: "alphanumeric",
        meta: { width: 300 },
      }),
      column.accessor("notified", {
        header: "Notified",
        cell: (prop) => formatNotified(users, prop.row.original),
        sortingFn: "alphanumeric",
      }),
      column.accessor("comment", {
        header: "Details",
        cell: (prop) => formatResolveDetails(users, prop.row.original),
        enableSorting: false,
      }),
      column.accessor("resolved", {
        header: "Resolve",
        cell: (prop) => {
          const value = prop.getValue();
          if (value) {
            return <BooleanIcon value={prop.getValue()} />;
          }
          return (
            <Button
              dataTestId="resolve-button"
              icon="checkmark"
              onClick={() => handleOpenSingle(prop.row.original, true)}
              variant="outlined"
            >
              Resolve
            </Button>
          );
        },
        enableSorting: false,
        meta: { width: 100 },
      }),
    ];

    if (!isGroupedTriggeredAlertsEnabled) {
      return baseColumns;
    }

    return [
      column.display({
        id: "priority",
        header: "Priority",
        cell: ({ row }) => (
          <IncidentPriority priority={getIncidentPriority(row.original)} />
        ),
        meta: { width: 150 },
      }),
      ...baseColumns,
    ];
  }, [
    getIncidentName,
    getIncidentPriority,
    handleOpenSingle,
    isGroupedTriggeredAlertsEnabled,
    users,
  ]);

  const bulkActionBarItems = useMemo<ReadonlyArray<BulkActionBarItem>>(
    () => [
      ...(isGroupedTriggeredAlertsEnabled && type !== "resolved"
        ? [
            {
              label: "Comment",
              onClick: () => handleOpenComment(),
            },
          ]
        : []),
      {
        label: "Resolve",
        onClick: () => handleOpenSelected(),
      },
    ],
    [
      handleOpenComment,
      handleOpenSelected,
      isGroupedTriggeredAlertsEnabled,
      type,
    ],
  );

  const bulkTableActions =
    userIsReadonly || type === "resolved"
      ? {}
      : {
          selectedItems,
          getIdentifier: ({ id }: Incident) => String(id),
          setSelectedItems,
        };

  const hiddenTableColumns = useMemo(() => {
    const columns = new Set(hiddenColumns);

    if (type === "resolved" || userIsReadonly) {
      columns.add("resolved");
    }

    if (type === "resolved" || !isGroupedTriggeredAlertsEnabled) {
      columns.add("status");
    }

    if (type !== "resolved") {
      columns.add("comment");
    }

    return [...columns];
  }, [hiddenColumns, isGroupedTriggeredAlertsEnabled, type, userIsReadonly]);

  return (
    <>
      {storesLoaded ? (
        isGroupedTriggeredAlertsEnabled && grouped ? (
          <GroupedIncidentsTable
            anchorRef={tableAnchorRef}
            bulkActionBarItems={bulkActionBarItems}
            handleOpenResolveAll={handleOpenResolveAll}
            handleOpenSingle={handleOpenSingle}
            incidents={filteredIncidents}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            sizePerPage={sizePerPage}
            type={type}
            userIsReadonly={userIsReadonly}
            users={users}
            {...tableFilterProps}
          />
        ) : (
          <DataTable
            bulkActionItems={bulkActionBarItems}
            columns={columns}
            getRowProps={
              isGroupedTriggeredAlertsEnabled
                ? (incident) => getIncidentRowProps(incident)
                : undefined
            }
            hideColumns={hiddenTableColumns}
            initialPageSize={sizePerPage}
            initialSort={{ id: "create_date", desc: true }}
            key={`${type}-${userIsReadonly}-${incidents.length > 0}`}
            paginationProps={{
              rowsPerPageOptions:
                sizePerPage == 5 ? [5, 10, 25, 50, 100] : [10, 25, 50, 100],
            }}
            rows={filteredIncidents}
            {...tableFilterProps}
            {...bulkTableActions}
          />
        )
      ) : (
        <LoadingIndicator />
      )}
      {showIncidentDrawer && selectedIncident && (
        <IncidentDetailsDrawer
          alertDetails={alerts[selectedIncident.alert_id] ?? null}
          canComment={!userIsReadonly}
          canEditStatus={!userIsReadonly}
          incident={selectedIncident}
          onClose={handleCloseIncidentDrawer}
          open
          showResolutionCommentByDefault={showIncidentDrawerWithComment}
          users={users}
        />
      )}
      {showResolveDrawer && incidentsToResolve.length > 0 && (
        <ResolveIncidentDrawer
          onCancel={handleClose}
          open
          selectedItems={incidentsToResolve}
          setSelectedItems={setSelectedItems}
        />
      )}
      {isGroupedTriggeredAlertsEnabled &&
        type !== "resolved" &&
        showCommentDrawer &&
        selectedItems.length > 0 && (
          <CommentIncidentsDrawer
            onCancel={handleCloseCommentDrawer}
            open
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        )}
    </>
  );
}
